import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, AlertTriangle, Plus, RefreshCw, Search } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatCurrency, formatDate, formatNumber, toPercent } from '@/lib/format';
import { cn } from '@/lib/utils';
import { RewardEditor } from './reward-editor';
import { useLoyaltyRewards } from './loyalty.api';
import {
  emptyReward,
  isPercentage,
  needsItem,
  statusOf,
  toInput,
  type LoyaltyReward,
  type LoyaltyRewardInput,
  type RewardItem,
  type RewardStatus,
} from './types';

/**
 * Loyalty rewards — what a customer can spend points on.
 *
 * The column worth having is the ceiling. `OrdersHelper` caps every redemption
 * at `MaxValue` regardless of type, so a reward with a ceiling of zero gives
 * nothing no matter what its value says — and the original created rewards that
 * way by default. Rows in that state are flagged here rather than sitting in
 * the list looking healthy.
 */

const ANY = 'ANY';

const STATUS_TONE: Record<RewardStatus, 'success' | 'secondary' | 'outline'> = {
  live: 'success',
  scheduled: 'secondary',
  ended: 'outline',
  inactive: 'outline',
};

export function LoyaltyPage() {
  const { t } = useTranslation();

  const [draft, setDraft] = useState('');
  const [status, setStatus] = useState(ANY);
  const [editing, setEditing] = useState<{
    values: LoyaltyRewardInput;
    item: RewardItem | null;
  } | null>(null);

  const list = useLoyaltyRewards();
  const rewards = useMemo(() => list.data ?? [], [list.data]);

  const counts = useMemo(() => {
    const tally: Record<RewardStatus, number> = {
      live: 0,
      scheduled: 0,
      ended: 0,
      inactive: 0,
    };
    for (const reward of rewards) tally[statusOf(reward)] += 1;
    return tally;
  }, [rewards]);

  const term = draft.trim().toLowerCase();
  const rows = useMemo(
    () =>
      rewards.filter((reward) => {
        if (status !== ANY && statusOf(reward) !== status) return false;
        if (!term) return true;
        return [reward.internalName, reward.name]
          .filter(Boolean)
          .some((value) => value!.toLowerCase().includes(term));
      }),
    [rewards, status, term]
  );

  const fields: FilterField[] = [
    {
      id: 'status',
      label: t('loyalty.status'),
      value: status,
      onChange: setStatus,
      sort: false,
      resetValue: ANY,
      options: [
        { value: ANY, label: t('loyalty.statusAny') },
        { value: 'live', label: `${t('loyalty.statusLive')} (${counts.live})` },
        {
          value: 'scheduled',
          label: `${t('loyalty.statusScheduled')} (${counts.scheduled})`,
        },
        {
          value: 'inactive',
          label: `${t('loyalty.statusInactive')} (${counts.inactive})`,
        },
        {
          value: 'ended',
          label: `${t('loyalty.statusEnded')} (${counts.ended})`,
        },
      ],
    },
  ];

  const valueLabel = (reward: LoyaltyReward) =>
    isPercentage(reward.type)
      ? `${formatNumber(toPercent(reward.value))}%`
      : formatCurrency(reward.value);

  const openEditor = (reward: LoyaltyReward | null) =>
    setEditing(
      reward
        ? { values: toInput(reward), item: reward.item }
        : { values: emptyReward(), item: null }
    );

  const StatusCell = ({ reward }: { reward: LoyaltyReward }) => {
    const state = statusOf(reward);
    return (
      <Badge variant={STATUS_TONE[state]}>
        {t(`loyalty.status${state[0].toUpperCase()}${state.slice(1)}`)}
      </Badge>
    );
  };

  /** A ceiling of zero silently zeroes the whole reward. */
  const CappedWarning = ({ reward }: { reward: LoyaltyReward }) =>
    reward.maxValue === 0 ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-destructive inline-flex items-center gap-1 text-xs">
            <AlertTriangle className="size-3.5" />
            {t('loyalty.givesNothing')}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {t('loyalty.givesNothingHint')}
        </TooltipContent>
      </Tooltip>
    ) : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('loyalty.title')}
        description={t('loyalty.subtitle')}
        actions={
          <>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={t('common.reload')}
              disabled={list.isFetching}
              onClick={() => void list.refetch()}
            >
              <RefreshCw className={list.isFetching ? 'animate-spin' : ''} />
            </Button>
            <Button size="sm" onClick={() => openEditor(null)}>
              <Plus />
              <span className="hidden sm:inline">{t('loyalty.newReward')}</span>
            </Button>
          </>
        }
      />

      <FilterBar
        busy={list.isFetching}
        search={
          <div className="relative flex-1">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={t('loyalty.searchPlaceholder')}
              aria-label={t('loyalty.searchPlaceholder')}
              className="h-10 pl-9"
            />
          </div>
        }
        fields={fields}
        summary={
          list.isSuccess ? (
            <p className="text-muted-foreground text-sm" aria-live="polite">
              {t('loyalty.showingOf', {
                shown: rows.length,
                total: rewards.length,
              })}
            </p>
          ) : null
        }
      />

      {list.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('loyalty.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void list.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {list.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-44" />
                <Skeleton className="h-3 w-52" />
              </div>
              <Skeleton className="h-5 w-16" />
            </div>
          ))}
        </div>
      )}

      {list.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={list.isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {rewards.length === 0 ? t('loyalty.none') : t('loyalty.noneMatch')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="hidden rounded-xl border lg:block">
            <Table busy={list.isFetching}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>{t('loyalty.reward')}</TableHead>
                  <TableHead className="text-right">
                    {t('loyalty.points')}
                  </TableHead>
                  <TableHead>{t('loyalty.type')}</TableHead>
                  <TableHead className="text-right">
                    {t('loyalty.worth')}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('loyalty.maxValue')}
                  </TableHead>
                  <TableHead>{t('loyalty.window')}</TableHead>
                  <TableHead>{t('loyalty.status')}</TableHead>
                  <TableHead className="w-20 text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((reward) => (
                  <TableRow key={reward.id}>
                    <TableCell>
                      <div className="min-w-0">
                        <p className="truncate font-medium">
                          {reward.name || '—'}
                        </p>
                        <p className="text-muted-foreground truncate text-xs">
                          {reward.internalName}
                        </p>
                        {needsItem(reward.type) && (
                          <p className="text-muted-foreground truncate text-xs">
                            {reward.item?.name ?? t('loyalty.noProduct')}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatNumber(reward.pointForRedeem)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {t(`loyalty.type${reward.type}`)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {valueLabel(reward)}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="tabular-nums">
                        {formatCurrency(reward.maxValue)}
                      </span>
                      <div>
                        <CappedWarning reward={reward} />
                      </div>
                    </TableCell>
                    <TableCell className="text-xs whitespace-nowrap">
                      <p>{formatDate(reward.startDate)}</p>
                      <p className="text-muted-foreground">
                        {formatDate(reward.endDate)}
                      </p>
                    </TableCell>
                    <TableCell>
                      <StatusCell reward={reward} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" onClick={() => openEditor(reward)}>
                        {t('loyalty.edit')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="relative lg:hidden">
            <BusyOverlay show={list.isFetching} />
            <div
              className={cn(
                'space-y-2',
                list.isFetching && 'opacity-60 transition-opacity'
              )}
            >
              {rows.map((reward) => (
                <div
                  key={reward.id}
                  className="bg-card space-y-3 rounded-xl border p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{reward.name || '—'}</p>
                      <p className="text-muted-foreground truncate text-xs">
                        {reward.internalName}
                      </p>
                    </div>
                    <StatusCell reward={reward} />
                  </div>
                  <p className="text-sm">
                    {t('loyalty.pointsFor', {
                      points: formatNumber(reward.pointForRedeem),
                      value: valueLabel(reward),
                    })}
                  </p>
                  <CappedWarning reward={reward} />
                  <p className="text-muted-foreground text-xs">
                    {formatDate(reward.startDate)} – {formatDate(reward.endDate)}
                  </p>
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => openEditor(reward)}
                  >
                    {t('loyalty.edit')}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <RewardEditor
        reward={editing?.values ?? null}
        attachedItem={editing?.item ?? null}
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
      />
    </div>
  );
}
